import React, { useState, useEffect } from 'react';
import { Search, User, Mail, Phone, ShoppingBag, Eye } from 'lucide-react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { COLLECTIONS } from '../../firebase/collections';

const CustomersManagement = () => {
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [customersSnapshot, ordersSnapshot] = await Promise.all([
        getDocs(collection(db, COLLECTIONS.CUSTOMERS)),
        getDocs(collection(db, COLLECTIONS.ORDERS))
      ]);

      const customersData = [];
      customersSnapshot.forEach((doc) => {
        customersData.push({ id: doc.id, ...doc.data() });
      });

      const ordersData = [];
      ordersSnapshot.forEach((doc) => {
        ordersData.push({ id: doc.id, ...doc.data() });
      });

      setCustomers(customersData);
      setOrders(ordersData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCustomerOrders = (customerEmail) => {
    return orders.filter(order => order.customerEmail === customerEmail);
  };

  const getCustomerStats = (customerEmail) => {
    const customerOrders = getCustomerOrders(customerEmail);
    return {
      totalOrders: customerOrders.length,
      completedOrders: customerOrders.filter(o => o.status === 'completed').length,
      totalSpent: customerOrders
        .filter(o => o.status === 'completed')
        .reduce((sum, o) => sum + (o.total || 0), 0)
    };
  };

  const filteredCustomers = customers.filter(customer =>
    customer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    customer.phone?.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">إدارة العملاء</h1>
          <p className="text-gray-600 mt-2">إجمالي العملاء: {customers.length}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md p-6">
        <div className="relative">
          <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="ابحث عن عميل (الاسم، البريد، الهاتف)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pr-10 pl-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 text-right"
          />
        </div>
      </div>

      {filteredCustomers.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredCustomers.map((customer) => {
            const stats = getCustomerStats(customer.email);
            return (
              <div key={customer.id} className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden">
                <div className="bg-gradient-to-r from-purple-500 to-pink-500 p-6 text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                      <User className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-lg">{String(customer.name || '')}</h3>
                      <p className="text-sm opacity-90">عميل نشط</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-white/20">
                    <div>
                      <p className="text-xs opacity-75">الطلبات</p>
                      <p className="text-xl font-bold">{stats.totalOrders}</p>
                    </div>
                    <div>
                      <p className="text-xs opacity-75">المبلغ الكلي</p>
                      <p className="text-xl font-bold">${stats.totalSpent}</p>
                    </div>
                  </div>
                </div>

                <div className="p-6 space-y-3">
                  <div className="flex items-center gap-3 text-gray-700">
                    <Mail className="w-4 h-4 text-purple-600" />
                    <p className="text-sm">{String(customer.email || '')}</p>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <Phone className="w-4 h-4 text-purple-600" />
                    <p className="text-sm">{String(customer.phone || 'غير متوفر')}</p>
                  </div>
                  <div className="flex items-center gap-3 text-gray-700">
                    <ShoppingBag className="w-4 h-4 text-purple-600" />
                    <p className="text-sm">طلبات مكتملة: {stats.completedOrders}</p>
                  </div>

                  <button
                    onClick={() => {
                      setSelectedCustomer({ ...customer, stats, orders: getCustomerOrders(customer.email) });
                      setShowDetailsModal(true);
                    }}
                    className="w-full mt-4 bg-purple-500 hover:bg-purple-600 text-white py-2 rounded-lg flex items-center justify-center gap-2 transition-colors"
                  >
                    <Eye className="w-4 h-4" />
                    عرض التفاصيل
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-md p-12 text-center text-gray-500">
          <User className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <p>لا يوجد عملاء</p>
        </div>
      )}

      {showDetailsModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl max-w-4xl w-full p-8 my-8">
            <div className="flex justify-between items-start mb-6">
              <h2 className="text-2xl font-bold text-gray-900">تفاصيل العميل</h2>
              <button
                onClick={() => setShowDetailsModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="grid md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <h3 className="font-bold text-lg text-gray-900">المعلومات الشخصية</h3>
                <div>
                  <p className="text-sm text-gray-600">الاسم</p>
                  <p className="font-medium">{selectedCustomer.name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">البريد الإلكتروني</p>
                  <p className="font-medium">{selectedCustomer.email}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">رقم الهاتف</p>
                  <p className="font-medium">{selectedCustomer.phone || 'غير متوفر'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">تاريخ التسجيل</p>
                  <p className="font-medium">
                    {selectedCustomer.createdAt?.toDate?.()?.toLocaleDateString('ar-EG') || 'غير محدد'}
                  </p>
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-bold text-lg text-gray-900">الإحصائيات</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                    <p className="text-sm text-blue-700">إجمالي الطلبات</p>
                    <p className="text-2xl font-bold text-blue-800">{selectedCustomer.stats.totalOrders}</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                    <p className="text-sm text-green-700">الطلبات المكتملة</p>
                    <p className="text-2xl font-bold text-green-800">{selectedCustomer.stats.completedOrders}</p>
                  </div>
                  <div className="col-span-2 bg-purple-50 rounded-lg p-4 border border-purple-200">
                    <p className="text-sm text-purple-700">إجمالي المبالغ</p>
                    <p className="text-3xl font-bold text-purple-800">${selectedCustomer.stats.totalSpent}</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="font-bold text-lg text-gray-900 mb-4">سجل الطلبات</h3>
              {selectedCustomer.orders.length > 0 ? (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {selectedCustomer.orders.map((order) => (
                    <div key={order.id} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-medium">طلب #{order.id.slice(0, 8)}</p>
                          <p className="text-sm text-gray-600">{order.type || 'عام'}</p>
                          <p className="text-xs text-gray-500 mt-1">
                            {order.createdAt?.toDate?.()?.toLocaleDateString('ar-EG')}
                          </p>
                        </div>
                        <div className="text-left">
                          <p className="text-lg font-bold text-yellow-600">${order.total || 0}</p>
                          <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mt-1 ${order.status === 'completed' ? 'bg-green-100 text-green-700' :
                              order.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-gray-100 text-gray-700'
                            }`}>
                            {order.status === 'completed' ? 'مكتمل' :
                              order.status === 'pending' ? 'قيد الانتظار' : order.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">لا توجد طلبات</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomersManagement;
